/**
 * Sneakerwear: shoppers pick a size before adding to cart.
 *
 * Product pages render with no size selected (inline script in snippets/variant-main-picker.liquid).
 * Pickers inserted later (Quick Add) are unselected here. Until a size is chosen, add-to-cart clicks
 * and submits are stopped with a message, and accelerated checkout buttons are hidden through the
 * .sw-size-pending class (assets/sw-fixes.css).
 */
(() => {
  if (window.swSizeGate) return;
  window.swSizeGate = true;

  const SIZE_NAMES = ['size', 'taille', 'pointure'];
  const GATED_PICKER = 'variant-picker[data-sw-size-gate]';
  const prepared = new WeakSet();

  /** @param {Element} input */
  const isSizeInput = (input) =>
    SIZE_NAMES.includes((input.getAttribute('data-option-name') || '').trim().toLowerCase());

  /** @param {Element} picker */
  const sizeInputs = (picker) =>
    Array.from(picker.querySelectorAll('fieldset input[type="radio"]')).filter(isSizeInput);

  /** @param {Element} picker */
  const hasSize = (picker) => sizeInputs(picker).some((input) => input instanceof HTMLInputElement && input.checked);

  /** @param {Element} element */
  const scopeOf = (element) => element.closest('quick-add-dialog, dialog, .shopify-section') || document.body;

  /** @param {Element} scope */
  const pendingPicker = (scope) => {
    const picker = scope.querySelector(GATED_PICKER);
    return picker && !hasSize(picker) ? picker : null;
  };

  /** @param {Element} scope */
  const sync = (scope) => {
    const pending = Boolean(pendingPicker(scope));
    scope.classList.toggle('sw-size-pending', pending);
    if (!pending) {
      // Hide the message but keep its space, so the buttons below do not jump under the pointer.
      scope.querySelectorAll('.sw-size-required').forEach((message) => message.classList.add('is-resolved'));
    }
  };

  /** @param {Element} picker */
  const prepare = (picker) => {
    if (prepared.has(picker)) return;
    prepared.add(picker);

    const isMainPicker =
      picker.getAttribute('data-template-product-match') === 'true' && !picker.closest('quick-add-dialog, dialog');

    if (!isMainPicker) {
      for (const input of sizeInputs(picker)) {
        if (!(input instanceof HTMLInputElement)) continue;
        input.checked = false;
        input.removeAttribute('checked');
        input.setAttribute('data-current-checked', 'false');
      }
    }

    sync(scopeOf(picker));
  };

  /** @param {Element} picker */
  const showMessage = (picker) => {
    const parent = picker.parentElement;
    let message = parent ? parent.querySelector(':scope > .sw-size-required') : null;

    if (!message) {
      message = document.createElement('p');
      message.className = 'sw-size-required';
      message.setAttribute('role', 'alert');
      picker.insertAdjacentElement('afterend', message);
    }

    message.classList.remove('is-resolved');
    message.textContent = picker.getAttribute('data-sw-size-msg') || 'Please select a size';
    picker.classList.remove('sw-size-shake');
    void (/** @type {HTMLElement} */ (picker).offsetWidth);
    picker.classList.add('sw-size-shake');
    picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  /**
   * @param {Event} event
   * @param {Element} scope
   */
  const stopIfPending = (event, scope) => {
    const picker = pendingPicker(scope);
    if (!picker) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    showMessage(picker);
  };

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const button = target ? target.closest('add-to-cart-component button, sticky-add-to-cart button, button[name="add"]') : null;
      if (!button) return;

      let scope = scopeOf(button);

      if (button.closest('sticky-add-to-cart') && !scope.querySelector(GATED_PICKER)) {
        const mainPicker = document.querySelector(`${GATED_PICKER}[data-template-product-match="true"]`);
        if (mainPicker) scope = scopeOf(mainPicker);
      }

      stopIfPending(event, scope);
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!(form.getAttribute('action') || '').includes('/cart/add')) return;
      stopIfPending(event, scopeOf(form));
    },
    true
  );

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !isSizeInput(target)) return;
    const picker = target.closest(GATED_PICKER);
    if (picker) sync(scopeOf(picker));
  });

  let scheduled = false;

  const scan = () => {
    scheduled = false;
    document.querySelectorAll(GATED_PICKER).forEach((picker) => {
      if (prepared.has(picker)) {
        sync(scopeOf(picker));
      } else {
        prepare(picker);
      }
    });
  };

  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(scan);
  };

  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['checked'],
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
})();
